import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import {
  Settings,
  Plus,
  Edit2,
  Trash2,
  Save,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
  Shield,
  Sliders
} from 'lucide-react';

const RISK_COLORS = {
  1: { bg: '#dcfce7', text: '#166534', label: 'Bajo' },
  2: { bg: '#d1fae5', text: '#047857', label: 'Medio-Bajo' },
  3: { bg: '#fef3c7', text: '#b45309', label: 'Medio' },
  4: { bg: '#fed7aa', text: '#c2410c', label: 'Medio-Alto' },
  5: { bg: '#fecaca', text: '#dc2626', label: 'Alto' }
};

export const MRCConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [factores, setFactores] = useState([]);
  const [opcionesPorFactor, setOpcionesPorFactor] = useState({});
  const [selectedFactor, setSelectedFactor] = useState(null);
  
  // Dialog states
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showAddOpcionDialog, setShowAddOpcionDialog] = useState(false);
  const [showEditOpcionDialog, setShowEditOpcionDialog] = useState(false);
  const [editingOpcion, setEditingOpcion] = useState(null);
  
  // Form states
  const [configForm, setConfigForm] = useState({});
  const [newOpcion, setNewOpcion] = useState({
    factor_id: '',
    codigo: '',
    descripcion: '',
    valor_riesgo: 3
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, factoresRes, opcionesRes] = await Promise.all([
        api.get('/mrc/config'),
        api.get('/mrc/factores'),
        api.get('/mrc/opciones')
      ]);
      
      setConfig(configRes.data);
      setConfigForm(configRes.data);
      setFactores(factoresRes.data);
      setOpcionesPorFactor(opcionesRes.data);
    } catch (error) {
      console.error('Error cargando MRC:', error);
      
      // Si no está inicializado, intentar inicializar
      if (error.response?.status === 404 || !error.response) {
        try {
          await api.post('/mrc/initialize');
          toast.success('MRC inicializado');
          loadData();
          return;
        } catch (initError) {
          toast.error('Error inicializando MRC');
        }
      } else {
        toast.error('Error cargando configuración MRC');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.patch('/mrc/config', {
        umbral_bajo: parseFloat(configForm.umbral_bajo),
        umbral_medio: parseFloat(configForm.umbral_medio),
        peso_score_operativo: parseFloat(configForm.peso_score_operativo),
        peso_score_mrc: parseFloat(configForm.peso_score_mrc)
      });
      toast.success('Configuración guardada');
      setShowConfigDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddOpcion = async () => {
    if (!newOpcion.descripcion || !newOpcion.factor_id) {
      toast.error('Completa los campos requeridos');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/mrc/opciones', newOpcion);
      toast.success('Opción agregada');
      setShowAddOpcionDialog(false);
      setNewOpcion({ factor_id: '', codigo: '', descripcion: '', valor_riesgo: 3 });
      loadData();
    } catch (error) {
      toast.error('Error al agregar opción');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpcion = async () => {
    if (!editingOpcion) return;
    
    setSaving(true);
    try {
      await api.patch(`/mrc/opciones/${editingOpcion.id}`, {
        codigo: editingOpcion.codigo,
        descripcion: editingOpcion.descripcion,
        valor_riesgo: editingOpcion.valor_riesgo
      });
      toast.success('Opción actualizada');
      setShowEditOpcionDialog(false);
      setEditingOpcion(null);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar opción');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOpcion = async (opcionId) => {
    if (!confirm('¿Eliminar esta opción?')) return;
    
    try {
      await api.delete(`/mrc/opciones/${opcionId}`);
      toast.success('Opción eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar opción');
    }
  };

  const openEditDialog = (opcion) => {
    setEditingOpcion({ ...opcion });
    setShowEditOpcionDialog(true);
  };

  const openAddDialog = (factorId) => {
    setNewOpcion({ ...newOpcion, factor_id: factorId });
    setShowAddOpcionDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando configuración MRC...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="mrc-config-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Matriz de Riesgo Cliente (MRC)
          </h1>
          <p className="text-slate-600 mt-1">
            Configuración de factores, ponderaciones y opciones de riesgo
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Button
            onClick={() => setShowConfigDialog(true)}
            variant="outline"
            style={{ borderColor: '#cbd5e1', color: '#374151' }}
            data-testid="config-btn"
          >
            <Sliders size={16} className="mr-2" />
            Configuración
          </Button>
          <Button
            onClick={loadData}
            variant="outline"
            style={{ borderColor: '#cbd5e1', color: '#374151' }}
          >
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Config Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white border-slate-200">
          <p className="text-sm text-slate-500">Umbral Bajo</p>
          <p className="text-2xl font-bold text-green-600">≤ {config?.umbral_bajo}</p>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <p className="text-sm text-slate-500">Umbral Medio</p>
          <p className="text-2xl font-bold text-yellow-600">≤ {config?.umbral_medio}</p>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <p className="text-sm text-slate-500">Peso Score Operativo</p>
          <p className="text-2xl font-bold text-blue-600">{(config?.peso_score_operativo * 100).toFixed(0)}%</p>
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <p className="text-sm text-slate-500">Peso Score MRC</p>
          <p className="text-2xl font-bold text-purple-600">{(config?.peso_score_mrc * 100).toFixed(0)}%</p>
        </Card>
      </div>

      {/* Factores */}
      <Card className="bg-white border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Settings size={20} className="text-blue-600" />
            Factores de Riesgo y Opciones
          </h3>
          <p className="text-sm text-slate-500">
            Haz clic en un factor para ver y editar sus opciones
          </p>
        </div>
        
        <Accordion type="single" collapsible className="w-full">
          {factores.map((factor) => {
            const factorData = opcionesPorFactor[factor.id];
            const opciones = factorData?.opciones || [];
            
            return (
              <AccordionItem key={factor.id} value={factor.id}>
                <AccordionTrigger className="px-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-slate-900">{factor.nombre}</span>
                      <span className="text-sm text-slate-500">
                        ({opciones.length} opciones)
                      </span>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                      {(factor.ponderacion * 100).toFixed(0)}%
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <p className="text-sm text-slate-600 mb-4">{factor.descripcion}</p>
                  
                  <div className="flex justify-end mb-3">
                    <Button
                      size="sm"
                      onClick={() => openAddDialog(factor.id)}
                      style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                    >
                      <Plus size={14} className="mr-1" />
                      Agregar Opción
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {opciones.map((opcion) => {
                      const riskStyle = RISK_COLORS[opcion.valor_riesgo] || RISK_COLORS[3];
                      
                      return (
                        <div 
                          key={opcion.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300"
                        >
                          <div className="flex items-center gap-3">
                            <span 
                              className="px-2 py-1 rounded text-xs font-bold"
                              style={{ backgroundColor: riskStyle.bg, color: riskStyle.text }}
                            >
                              {opcion.valor_riesgo}
                            </span>
                            <div>
                              <p className="font-medium text-slate-900">{opcion.descripcion}</p>
                              {opcion.codigo && (
                                <p className="text-xs text-slate-500">Código: {opcion.codigo}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(opcion)}
                              className="text-slate-600"
                            >
                              <Edit2 size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOpcion(opcion.id)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Card>

      {/* Dialog Configuración General */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="text-slate-900">Configuración MRC</DialogTitle>
            <DialogDescription className="text-slate-600">
              Ajusta los umbrales y pesos del scoring
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Umbral Bajo (≤)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  max="5"
                  value={configForm.umbral_bajo || ''}
                  onChange={(e) => setConfigForm({ ...configForm, umbral_bajo: e.target.value })}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
              <div>
                <Label className="text-slate-700">Umbral Medio (≤)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  max="5"
                  value={configForm.umbral_medio || ''}
                  onChange={(e) => setConfigForm({ ...configForm, umbral_medio: e.target.value })}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Peso Score Operativo</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={configForm.peso_score_operativo || ''}
                  onChange={(e) => setConfigForm({ ...configForm, peso_score_operativo: e.target.value })}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
              <div>
                <Label className="text-slate-700">Peso Score MRC</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={configForm.peso_score_mrc || ''}
                  onChange={(e) => setConfigForm({ ...configForm, peso_score_mrc: e.target.value })}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Los pesos deben sumar 1.0 (100%)
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)} style={{ color: '#374151' }}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={saving} style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>
              <Save size={16} className="mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Agregar Opción */}
      <Dialog open={showAddOpcionDialog} onOpenChange={setShowAddOpcionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="text-slate-900">Agregar Opción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Código (opcional)</Label>
              <Input
                value={newOpcion.codigo}
                onChange={(e) => setNewOpcion({ ...newOpcion, codigo: e.target.value })}
                placeholder="Ej: 01, PH-MONO..."
                style={{ backgroundColor: '#ffffff' }}
              />
            </div>
            <div>
              <Label className="text-slate-700">Descripción *</Label>
              <Input
                value={newOpcion.descripcion}
                onChange={(e) => setNewOpcion({ ...newOpcion, descripcion: e.target.value })}
                placeholder="Descripción de la opción..."
                style={{ backgroundColor: '#ffffff' }}
              />
            </div>
            <div>
              <Label className="text-slate-700">Valor de Riesgo (1-5) *</Label>
              <Select 
                value={String(newOpcion.valor_riesgo)} 
                onValueChange={(v) => setNewOpcion({ ...newOpcion, valor_riesgo: parseInt(v) })}
              >
                <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(v => (
                    <SelectItem key={v} value={String(v)}>
                      {v} - {RISK_COLORS[v].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOpcionDialog(false)} style={{ color: '#374151' }}>
              Cancelar
            </Button>
            <Button onClick={handleAddOpcion} disabled={saving} style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Opción */}
      <Dialog open={showEditOpcionDialog} onOpenChange={setShowEditOpcionDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="text-slate-900">Editar Opción</DialogTitle>
          </DialogHeader>
          {editingOpcion && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-700">Código</Label>
                <Input
                  value={editingOpcion.codigo || ''}
                  onChange={(e) => setEditingOpcion({ ...editingOpcion, codigo: e.target.value })}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
              <div>
                <Label className="text-slate-700">Descripción *</Label>
                <Input
                  value={editingOpcion.descripcion}
                  onChange={(e) => setEditingOpcion({ ...editingOpcion, descripcion: e.target.value })}
                  style={{ backgroundColor: '#ffffff' }}
                />
              </div>
              <div>
                <Label className="text-slate-700">Valor de Riesgo (1-5) *</Label>
                <Select 
                  value={String(editingOpcion.valor_riesgo)} 
                  onValueChange={(v) => setEditingOpcion({ ...editingOpcion, valor_riesgo: parseInt(v) })}
                >
                  <SelectTrigger style={{ backgroundColor: '#ffffff' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(v => (
                      <SelectItem key={v} value={String(v)}>
                        {v} - {RISK_COLORS[v].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditOpcionDialog(false)} style={{ color: '#374151' }}>
              Cancelar
            </Button>
            <Button onClick={handleEditOpcion} disabled={saving} style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MRCConfig;
